'use client';

/**
 * Spec 107 §4.2 — the copyable install snippets with a client switcher.
 *
 * One tab per install client (Claude Code, Claude Desktop, Cursor, VS
 * Code, raw JSON); under the active tab, one block per distribution.
 * The snippets are generated by `@repo/mcp-catalog` from structured
 * transport data and arrive as props — this component never composes
 * a command itself. Copy uses the transitions.dev icon-swap the
 * `PromptCopy` primitive established (both glyphs mounted, cross-fade).
 */
import type { McpDirectoryDistribution } from '@repo/control-plane-domain';
import { cn, Icon } from '@repo/marketing-ui';
import { Button } from '@repo/ui/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@repo/ui/components/ui/tabs';
import { useEffect, useRef, useState } from 'react';
import { MCP_CLIENTS, MCP_DIRECTORY } from '@/lib/mcp-directory-copy';

const SWAP_BASE =
  'col-start-1 row-start-1 transition-[opacity,filter,scale] duration-[250ms] ease-in-out motion-reduce:transition-none';
const SWAP_IN = 'scale-100 opacity-100 blur-0';
const SWAP_OUT = 'scale-[0.25] opacity-0 blur-[2px]';

function CopySnippet({ code, label }: { code: string; label: string }) {
  const [copied, setCopied] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    [],
  );
  async function copy() {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard unavailable — the code stays selectable.
    }
  }
  return (
    <Button
      type="button"
      variant="ghost"
      size="icon-sm"
      onClick={copy}
      aria-label={label}
      className={cn(
        'absolute top-2 right-2 grid size-9 place-items-center',
        'bg-surface-card-primary hover:bg-surface-hover-1 active:bg-surface-click-1',
        'transition-[color] motion-reduce:transition-none',
        copied ? 'text-icon-success duration-75' : 'text-icon-primary duration-1000',
      )}
    >
      <span className="grid">
        <Icon name="copy" size={20} className={cn(SWAP_BASE, copied ? SWAP_OUT : SWAP_IN)} />
        <Icon name="check" size={20} className={cn(SWAP_BASE, copied ? SWAP_IN : SWAP_OUT)} />
      </span>
      <span aria-live="polite" className="sr-only">
        {copied ? 'Snippet copied to clipboard' : ''}
      </span>
    </Button>
  );
}

export function distributionHeading(d: McpDirectoryDistribution): string {
  if (d.kind === 'remote') return `Hosted — ${d.coordinate}`;
  return `${d.kind} · ${d.coordinate} · ${MCP_DIRECTORY.labels.version.toLowerCase()} ${d.version}`;
}

const CONNECT0_TAB = MCP_DIRECTORY.connect0Tab;

export function InstallSnippets({
  distributions,
  serverName,
  serverSlug,
  needsCredentials = false,
}: {
  distributions: McpDirectoryDistribution[];
  serverName: string;
  /** Spec 118 §4 — carried into the dashboard by the connect0 tab. */
  serverSlug?: string;
  needsCredentials?: boolean;
}) {
  const available = MCP_CLIENTS.filter((c) => distributions.some((d) => d.install.some((s) => s.client === c.id)));
  // Spec 118 §4 (O1) — connect0 leads. It renders for every server, even
  // one with no snippets at all: "run it here" does not depend on a
  // publisher having shipped a config for someone else's client.
  const showConnect0 = Boolean(serverSlug);
  const [client, setClient] = useState<string>(showConnect0 ? CONNECT0_TAB.id : (available[0]?.id ?? 'claude-code'));
  if (available.length === 0 && !showConnect0) return null;
  return (
    <Tabs value={client} onValueChange={setClient} className="flex flex-col gap-4">
      {/* CON-1273: the client switcher is the connected-pill Tabs from @repo/ui. */}
      <TabsList aria-label="Install client">
        {(showConnect0 ? [{ id: CONNECT0_TAB.id, label: CONNECT0_TAB.label }, ...available] : available).map((c) => (
          <TabsTrigger key={c.id} value={c.id}>
            {c.label}
          </TabsTrigger>
        ))}
      </TabsList>
      <TabsContent value={client} className="mt-0 flex flex-col gap-6">
        {client === CONNECT0_TAB.id ? (
          <div className="flex flex-col items-start gap-4 rounded-xxs border border-border-primary bg-surface-card-primary p-6">
            <p className="font-body text-body-sm text-text-body">{CONNECT0_TAB.pitch}</p>
            {needsCredentials ? (
              <p className="font-label text-label-sm text-text-caption-1">{CONNECT0_TAB.credentialsNote}</p>
            ) : null}
            <a
              // The connect0 tab exists only with a slug.
              href={`/use-mcp/${encodeURIComponent(serverSlug as string)}`}
              className={cn(
                'rounded-xl px-6 py-3 font-label font-semibold text-label-md',
                'bg-surface-action-1 text-text-on-action-1 hover:bg-surface-action-hover-1 active:bg-surface-action-click-1',
              )}
            >
              {CONNECT0_TAB.cta}
            </a>
            <p className="font-label text-label-sm text-text-caption-1">{CONNECT0_TAB.signedOutNote}</p>
          </div>
        ) : null}
        {distributions.map((d) => {
          const snippet = d.install.find((s) => s.client === client);
          if (!snippet) return null;
          return (
            <div key={`${d.kind}:${d.coordinate}`} className="flex flex-col gap-2">
              <p className="font-label text-label-sm text-text-caption-1">
                {distributionHeading(d)}
                {snippet.path ? (
                  <>
                    {' '}
                    · <code className="font-mono">{snippet.path}</code>
                  </>
                ) : null}
              </p>
              <div className="relative">
                <pre className="overflow-x-auto rounded-xxs border border-border-primary bg-surface-card-primary p-4 pr-14 font-mono text-sm leading-6 text-text-body">
                  <code data-language={snippet.language}>{snippet.code}</code>
                </pre>
                <CopySnippet
                  code={snippet.code}
                  label={`Copy the ${serverName} snippet for ${MCP_CLIENTS.find((c) => c.id === client)?.label}`}
                />
              </div>
            </div>
          );
        })}
      </TabsContent>
    </Tabs>
  );
}
